// API client for the FastAPI backend (docs/mobile/API_CONTRACT.md is the
// contract; the web and the Flutter app share it).

import { decodeVariant } from './productVariants';

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://anum360-store.onrender.com').replace(/\/+$/, '');
export const API_URL = `${BACKEND_URL}/api`;

// ── Errors ────────────────────────────────────────────────────────────
// FastAPI errors are {detail: "..."} or {detail: [{loc, msg, type}]} (422);
// returns/reviews/orders also send a stable `code` (API_CONTRACT §6c).

export const detailText = (data) => {
  if (!data || typeof data !== 'object') return '';
  const { detail } = data;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body' && p !== 'query').join('.') : '';
        return field ? `${field}: ${d?.msg}` : d?.msg;
      })
      .filter(Boolean)
      .join('; ');
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  if (typeof data.message === 'string') return data.message;
  return '';
};

/**
 * Human message for a failed API response. 401/403/429/5xx get a clear
 * sentence (the server text is kept when it is more specific); 404/409/422
 * show the server's reason.
 */
export const apiErrorMessage = (status, data) => {
  const detail = detailText(data);
  switch (status) {
    case 400:
      return detail || 'The request was not accepted. Please check and try again.';
    case 401:
      if (/two-factor/i.test(detail)) return detail;
      if (/invalid credentials/i.test(detail)) return 'Incorrect email or password.';
      if (/password/i.test(detail)) return detail; // "Invalid email or password", "Incorrect password"
      return 'Your session has expired. Please sign in again.';
    case 403:
      if (/disabled/i.test(detail)) return 'This account has been disabled.';
      if (/read-only/i.test(detail)) return 'Your account is read-only, so this change was not saved.';
      if (/ask the owner/i.test(detail)) return detail; // 403 escalate_to_owner (operations role)
      return "You don't have permission to do that.";
    case 404:
      return detail || 'Not found.';
    case 409:
      return detail || 'This was changed by someone else. Refresh and try again.';
    case 422:
      return detail ? `Please check the form: ${detail}` : 'Please check the form and try again.';
    case 423:
      return 'Too many failed attempts. This account is locked for a while. Try again later.';
    case 429:
      return 'Too many attempts. Please wait a few minutes and try again.';
    default:
      if (status >= 500) return 'The server had a problem. Please try again in a moment.';
      return detail || `Request failed (${status}).`;
  }
};

/** Parse a fetch Response; throw an Error carrying status/code/detail on failure. */
export const handleResponse = async (response) => {
  const text = await response.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-JSON body (HTML error page, gateway error) */ }
  if (!response.ok) {
    const err = new Error(apiErrorMessage(response.status, data));
    err.status = response.status;
    err.detail = detailText(data) || (typeof text === 'string' ? text.slice(0, 200) : '');
    err.code = (data && data.code) || response.headers?.get?.('X-Error-Code') || undefined;
    err.body = data ?? text;
    throw err;
  }
  return data;
};

const bearer = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

// ── Network failures ──────────────────────────────────────────────────
// fetch() rejects with a TypeError ("Failed to fetch" in Chrome, "Load failed"
// in Safari, "NetworkError…" in Firefox) when the server is unreachable, the
// connection drops or CORS blocks the response. That text must never reach a
// customer: these helpers turn it into NETWORK_ERROR_TEXT.

export const NETWORK_ERROR_TEXT = {
  en: "We couldn't reach our boutique. Please check your connection and try again.",
  ar: 'تعذر الاتصال بمتجرنا. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
};

export const isNetworkError = (err) => {
  if (!err) return false;
  if (err.network === true) return true;
  if (err.status) return false; // an HTTP response arrived
  if (err.name === 'TimeoutError') return true;
  const msg = String(err.message || '');
  return err.name === 'TypeError'
    || /failed to fetch|load failed|networkerror|network request failed/i.test(msg);
};

const networkError = (cause) => {
  const err = new Error(NETWORK_ERROR_TEXT.en);
  err.network = true;
  err.cause = cause;
  return err;
};

/** fetch() that rethrows network failures as an Error with `network: true`
 *  and a readable message (aborts by the caller are passed through). */
export const safeFetch = async (url, init) => {
  try {
    return await fetch(url, init);
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw isNetworkError(err) ? networkError(err) : err;
  }
};

const RETRYABLE_STATUS = new Set([502, 503, 504]);
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

// Defaults for getJson (tests set retryDelayMs to 0).
export const NETWORK_DEFAULTS = { timeoutMs: 25000, retries: 1, retryDelayMs: 1500 };

const abortError = () => new DOMException('Aborted', 'AbortError');

/** One GET with a per-attempt timeout and one retry (see getJson). */
const fetchJsonWithRetry = async (url, headers, signal, { timeoutMs, retries, retryDelayMs }) => {
  for (let attempt = 0; ; attempt += 1) {
    if (signal.aborted) throw abortError();
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
    const onAbort = () => controller.abort();
    signal.addEventListener('abort', onAbort);
    let response;
    try {
      response = await fetch(url, { headers, signal: controller.signal });
    } catch (err) {
      if (signal.aborted) throw err;
      if (attempt < retries && (timedOut || isNetworkError(err))) {
        await sleep(retryDelayMs);
        continue; // eslint-disable-line no-continue
      }
      throw networkError(err);
    } finally {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
    }
    if (RETRYABLE_STATUS.has(response.status) && attempt < retries) {
      await sleep(retryDelayMs);
      continue; // eslint-disable-line no-continue
    }
    return handleResponse(response);
  }
};

// key → {promise, controller, subscribers}: identical GETs in flight.
const inflightGets = new Map();
// key → {promise, expires}: answers started by primeGetJson (route data
// fetched while the route's code chunk still downloads), taken once.
const primedGets = new Map();
export const PRIME_TTL_MS = 15000;

const getKey = (url, headers) => `${url}|${headers.Authorization || ''}`;

// Answers the inline script in public/index.html started before the app
// booted (shop / product pages): {url: Promise<parsed JSON | undefined>}.
// Anonymous requests only, each taken once; `undefined` (a failed or non-OK
// answer) makes the caller fetch normally, with its usual errors and retry.
const takeEarly = (url, headers) => {
  if (headers.Authorization || typeof window === 'undefined') return null;
  const early = window.__anumEarly;
  if (!early || !Object.prototype.hasOwnProperty.call(early, url)) return null;
  const promise = early[url];
  delete early[url];
  return promise && typeof promise.then === 'function' ? promise : null;
};

const takePrimed = (key) => {
  const primed = primedGets.get(key);
  if (!primed) return null;
  primedGets.delete(key);
  return primed.expires > Date.now() ? primed.promise : null;
};

// ── Public, anonymous reads (short memo) ──────────────────────────────
// Store facts, categories, collections, banners, payment methods: the same
// answer for every visitor, asked by several components on every page. An
// anonymous getJson of one of these URLs is kept for PUBLIC_GET_TTL_MS (like
// the server's own 60 s public cache); failures are never kept. They are sent
// without Authorization, so the browser may also reuse the backend's
// `Cache-Control: public` answer.
export const PUBLIC_GET_TTL_MS = 60000;
const PUBLIC_GET_PATHS = /^\/(categories|collections|banners\/active|settings\/public\/store|settings\/shipping\/zones|payments\/methods)(\?|$)/;
const publicMemo = new Map();

const memoKey = (url, headers) => {
  if (headers.Authorization || !url.startsWith(API_URL)) return null;
  return PUBLIC_GET_PATHS.test(url.slice(API_URL.length)) ? url : null;
};

/**
 * GET with a per-attempt timeout and one automatic retry on a network error,
 * timeout or 502/503/504 (Render cold starts take up to ~50 s). Only for
 * idempotent reads. Identical concurrent GETs (same URL + auth) share one
 * request, with or without an AbortSignal: a caller's signal rejects only
 * that caller, and the request itself is aborted once every caller that
 * passed a signal has aborted (and no caller without one is waiting).
 * Resolves to the parsed JSON; rejects like handleResponse, or with
 * `network: true` when the server could not be reached.
 */
export const getJson = (url, {
  headers = {}, signal,
  timeoutMs = NETWORK_DEFAULTS.timeoutMs,
  retries = NETWORK_DEFAULTS.retries,
  retryDelayMs = NETWORK_DEFAULTS.retryDelayMs,
} = {}) => {
  if (signal?.aborted) return Promise.reject(abortError());
  const memo = memoKey(url, headers);
  const hit = memo && publicMemo.get(memo);
  if (hit && hit.expires > Date.now()) return Promise.resolve(hit.value);
  const key = getKey(url, headers);
  const primed = takePrimed(key);
  let entry = inflightGets.get(key);
  if (!entry) {
    const controller = new AbortController();
    const fetchIt = () => fetchJsonWithRetry(url, headers, controller.signal, { timeoutMs, retries, retryDelayMs });
    const early = primed ? null : takeEarly(url, headers);
    let promise = primed
      || (early ? early.then((value) => (value === undefined ? fetchIt() : value), fetchIt) : fetchIt());
    if (memo) {
      promise = promise.then((value) => {
        publicMemo.set(memo, { value, expires: Date.now() + PUBLIC_GET_TTL_MS });
        return value;
      });
    }
    entry = { promise, controller, subscribers: 0 };
    inflightGets.set(key, entry);
    const current = entry;
    const clear = () => { if (inflightGets.get(key) === current) inflightGets.delete(key); };
    promise.then(clear, clear);
  }
  entry.subscribers += 1;
  if (!signal?.addEventListener) return entry.promise; // never aborts: holds the request open

  const shared = entry;
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      reject(abortError());
      shared.subscribers -= 1;
      if (shared.subscribers <= 0) {
        if (inflightGets.get(key) === shared) inflightGets.delete(key);
        shared.controller.abort();
      }
    };
    signal.addEventListener('abort', onAbort, { once: true });
    shared.promise.then(
      (value) => { signal.removeEventListener('abort', onAbort); resolve(value); },
      (err) => { signal.removeEventListener('abort', onAbort); reject(err); },
    );
  });
};

/**
 * Start a GET now so the page that needs it finds it in flight (or, for
 * PRIME_TTL_MS, already answered) instead of starting it after its code
 * chunk has loaded. Same URL + headers as the page's own getJson call.
 */
export const primeGetJson = (url, options = {}) => {
  const key = getKey(url, options.headers || {});
  if (primedGets.has(key) || inflightGets.has(key)) return;
  const promise = getJson(url, options);
  primedGets.set(key, { promise, expires: Date.now() + PRIME_TTL_MS });
  // A failed prefetch is forgotten; the page asks again and shows its own error.
  promise.catch(() => { if (primedGets.get(key)?.promise === promise) primedGets.delete(key); });
};

/**
 * The same memo for a loader of public facts (store details, delivery zones,
 * payment methods) that parses the answer itself: `loader()` runs once per
 * PUBLIC_GET_TTL_MS for `key`, concurrent callers share it, and a rejection
 * is never kept (the loader throws on a failed answer).
 */
const memoInflight = new Map();
export const memoPublic = (key, loader) => {
  const hit = publicMemo.get(key);
  if (hit && hit.expires > Date.now()) return Promise.resolve(hit.value);
  const pending = memoInflight.get(key);
  if (pending) return pending;
  const promise = Promise.resolve().then(loader).then((value) => {
    publicMemo.set(key, { value, expires: Date.now() + PUBLIC_GET_TTL_MS });
    return value;
  });
  memoInflight.set(key, promise);
  const clear = () => { if (memoInflight.get(key) === promise) memoInflight.delete(key); };
  promise.then(clear, clear);
  return promise;
};

/** GET of a public, anonymous endpoint (`path` after /api), memoised as above. */
export const getPublicJson = (path, { signal } = {}) => getJson(`${API_URL}${path}`, { signal });

export const clearPublicGetCache = () => {
  flashSalesAnswer = null;
  publicMemo.clear();
  memoInflight.clear();
  primedGets.clear();
};

// ── Admin session ─────────────────────────────────────────────────────
// Every admin request goes through adminFetch. A 401 means the admin token is
// no longer valid (expired, revoked by a password change, account disabled):
// it is removed at once and AdminContext is told to sign out.

export const ADMIN_TOKEN_KEY = 'admin_token';
export const ADMIN_DATA_KEY = 'admin_data';
export const ADMIN_SESSION_EXPIRED = 'anum360:admin-session-expired';

export const clearAdminSession = () => {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_DATA_KEY);
  } catch { /* storage unavailable */ }
};

const sentBearer = (headers) => {
  if (!headers) return false;
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : (headers.Authorization || headers.authorization);
  return /^Bearer\s+\S/.test(value || '');
};

/** fetch() for admin endpoints: clears the admin session on a 401 and on a
 * 403 `2fa_required` (the account's 2FA grace is over). */
export const ADMIN_FORBIDDEN = 'anum360:admin-forbidden';
/** sessionStorage: why the admin was sent back to sign-in ('2fa_required'). */
export const ADMIN_SIGNIN_REASON_KEY = 'anum_admin_signin_reason';

export const adminFetch = async (url, init = {}) => {
  const response = await fetch(url, init);
  if (response.status === 401 && sentBearer(init.headers)) {
    clearAdminSession();
    try { window.dispatchEvent(new Event(ADMIN_SESSION_EXPIRED)); } catch { /* non-browser */ }
  } else if (response.status === 403 && sentBearer(init.headers)) {
    let code;
    try { code = (await response.clone().json())?.detail?.code; } catch { /* not JSON */ }
    if (code === '2fa_required') {
      // 2FA grace over: end the session; signing in again opens the setup.
      clearAdminSession();
      try { sessionStorage.setItem(ADMIN_SIGNIN_REASON_KEY, code); } catch { /* storage blocked */ }
      try { window.dispatchEvent(new Event(ADMIN_SESSION_EXPIRED)); } catch { /* non-browser */ }
      return response;
    }
    // Access may have changed: AdminContext re-reads GET /admin/me.
    try { window.dispatchEvent(new Event(ADMIN_FORBIDDEN)); } catch { /* non-browser */ }
  }
  return response;
};

const queryString = (query) => {
  if (!query) return '';
  const qs = new URLSearchParams(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return qs ? `?${qs}` : '';
};

export const adminRequest = async (token, path, { method = 'GET', body, query } = {}) => {
  const headers = { ...bearer(token) };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await adminFetch(`${API_URL}${path}${queryString(query)}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  return handleResponse(response);
};

// ── Catalog ───────────────────────────────────────────────────────────
export const getCategories = async ({ signal } = {}) => getPublicJson('/categories', { signal });

export const getCategory = async (categoryId) =>
  handleResponse(await fetch(`${API_URL}/categories/${encodeURIComponent(categoryId)}`));

// GET /products and /products/{id} are public, but the token matters:
//  * a customer token lets the backend add the caller's own tier price
//    (pricing.tier / pricing.tier_price, e.g. VIP) — see API_CONTRACT §0;
//  * an admin token returns full documents including hidden products.
// `token` undefined → the storefront customer token (if any) is sent;
// admin pages pass the admin token explicitly; null → anonymous.
const productAuthHeaders = (token) => {
  let value = token;
  if (value === undefined) {
    try { value = localStorage.getItem('token'); } catch { value = null; }
  }
  return bearer(value);
};

// GET /products/suggest?q= — search-box type-ahead (API_CONTRACT §2):
// {query, products (≤ 8, public shape, one image), brands [{name, count}],
// categories [{id, slug, name}], total}. Pass an AbortSignal to cancel it
// when the customer types on.
export const getSearchSuggestions = async (q, { signal } = {}) =>
  getJson(`${API_URL}/products/suggest?q=${encodeURIComponent(q)}`, { signal, timeoutMs: 15000 });

// GET /products/for-you — the home "For you" feed (API_CONTRACT §2, G65):
// {items: [product + reason + reason_detail], next_cursor, personalised,
// source}. `seen` / `cats`: this device's view history (utils/recentlyViewed);
// the customer token (if any) blends in their wishlist, orders, colours, size.
export const getForYouFeed = async ({ limit, cursor, seen = [], cats = [] } = {}, { signal } = {}) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit);
  if (cursor) params.append('cursor', cursor);
  if (seen.length) params.append('seen', seen.slice(0, 50).join(','));
  if (cats.length) params.append('cats', cats.slice(0, 10).join(','));
  const qs = params.toString();
  return getJson(`${API_URL}/products/for-you${qs ? `?${qs}` : ''}`, {
    headers: productAuthHeaders(),
    ...(signal ? { signal } : {}),
  });
};

/** The GET /products query string for a filter object (fixed parameter order). */
export const productsQuery = (filters = {}) => {
  const params = new URLSearchParams();
  const add = (key, value) => {
    if (value !== undefined && value !== null && value !== '') params.append(key, value);
  };
  add('category_id', filters.category_id);
  add('cat', filters.cat);
  add('is_featured', filters.is_featured);
  add('search', filters.search);
  add('skip', filters.skip);
  add('limit', filters.limit);
  add('min_price', filters.min_price);
  add('max_price', filters.max_price);
  add('brands', filters.brands);
  add('colors', filters.colors);
  add('sizes', filters.sizes);
  add('in_stock', filters.in_stock);
  if (filters.on_sale) add('on_sale', 'true');
  if (filters.min_rating) add('min_rating', filters.min_rating);
  add('sort', filters.sort);
  if (filters.with_total) add('with_total', filters.with_total);
  return params.toString();
};

export const getProducts = async (filters = {}, token, { signal } = {}) =>
  getJson(`${API_URL}/products?${productsQuery(filters)}`, {
    headers: productAuthHeaders(token),
    ...(signal ? { signal } : {}),
  });

/** Start the storefront's GET /products (same URL + auth as getProducts) now. */
export const prefetchProducts = (filters) => {
  primeGetJson(`${API_URL}/products?${productsQuery(filters)}`, { headers: productAuthHeaders(undefined) });
};

// GET /products/facets (G12): filter options with counts for the active
// products matching `cat` / `search` → {brands, colors, sizes: [{name, count}],
// price: {min, max}, total}. `on_sale`: only products on sale.
export const getProductFacets = async ({ cat, search, on_sale: onSale } = {}) => {
  const params = new URLSearchParams();
  if (cat) params.append('cat', cat);
  if (search) params.append('search', search);
  if (onSale) params.append('on_sale', 'true');
  const qs = params.toString();
  return getJson(`${API_URL}/products/facets${qs ? `?${qs}` : ''}`);
};

export const productUrl = (productId) => `${API_URL}/products/${encodeURIComponent(productId)}`;

// One attempt (the product page offers its own "Try again"); shared with a
// route prefetch of the same product.
export const getProduct = async (productId, token) =>
  getJson(productUrl(productId), { headers: productAuthHeaders(token), retries: 0 });

/** Start the storefront's GET /products/{id} (same URL + auth as getProduct) now. */
export const prefetchProduct = (productId) => {
  if (productId) primeGetJson(productUrl(productId), { headers: productAuthHeaders(undefined), retries: 0 });
};

// ── Newsletter ────────────────────────────────────────────────────────
// POST /promotions/email-signup. The welcome code is emailed to the
// subscriber and is never in the response (S5).
// Resolves to 'subscribed' | 'subscribed_no_email' (the welcome e-mail could
// not be sent: never claim the code is in the inbox) | 'already' | 'invalid'
// | 'rate_limited' | 'error'.
export const subscribeEmail = async (email, source) => {
  try {
    const response = await fetch(`${API_URL}/promotions/email-signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source }),
    });
    if (response.status === 429) return 'rate_limited';
    if (response.status === 400 || response.status === 422) return 'invalid';
    if (!response.ok) return 'error';
    const data = await response.json().catch(() => null);
    if (data?.already_subscribed) return 'already';
    return data?.coupon_sent === false ? 'subscribed_no_email' : 'subscribed';
  } catch {
    return 'error';
  }
};

// Shopper-facing text for each subscribeEmail() result ({en, ar} for t()).
export const NEWSLETTER_MESSAGES = {
  subscribed: {
    ok: true,
    text: {
      en: 'You are in. Your welcome code is in your inbox.',
      ar: 'تم الاشتراك. أرسلنا رمز الترحيب إلى بريدك الإلكتروني.',
    },
  },
  subscribed_no_email: {
    ok: true,
    text: { en: 'You are in. Thank you for subscribing.', ar: 'تم الاشتراك. شكراً لك.' },
  },
  already: {
    ok: true,
    text: { en: 'This email is already subscribed.', ar: 'هذا البريد مشترك بالفعل.' },
  },
  invalid: {
    ok: false,
    text: { en: 'Please enter a valid email address.', ar: 'يرجى إدخال بريد إلكتروني صحيح.' },
  },
  rate_limited: {
    ok: false,
    text: { en: 'Too many attempts. Please try again later.', ar: 'محاولات كثيرة. حاول لاحقاً.' },
  },
  error: {
    ok: false,
    text: { en: 'Subscription failed. Please try again.', ar: 'تعذر الاشتراك. حاول مرة أخرى.' },
  },
};

export const newsletterMessage = (result) => NEWSLETTER_MESSAGES[result] || NEWSLETTER_MESSAGES.error;

// ── Enquiry forms ─────────────────────────────────────────────────────
// POST /contact (public, rate-limited). Resolves to
// 'sent' | 'invalid' | 'rate_limited' | 'error'.
export const sendContactMessage = async (payload) => {
  try {
    const response = await fetch(`${API_URL}/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (response.ok) return 'sent';
    if (response.status === 429) return 'rate_limited';
    if (response.status === 400 || response.status === 422) return 'invalid';
    return 'error';
  } catch {
    return 'error';
  }
};

// Shopper-facing text ({en, ar}) for a failed sendContactMessage() result.
export const contactErrorMessage = (result) => {
  if (result === 'invalid') {
    return { en: 'Please enter a valid email address and a short message.', ar: 'يرجى إدخال بريد إلكتروني صحيح ورسالة قصيرة.' };
  }
  if (result === 'rate_limited') {
    return { en: 'Too many messages. Please wait a few minutes, or message us on WhatsApp.', ar: 'رسائل كثيرة. انتظر بضع دقائق أو راسلنا عبر واتساب.' };
  }
  return { en: "We couldn't send your message. Please try again or message us on WhatsApp.", ar: 'تعذر إرسال رسالتك. حاول مرة أخرى أو راسلنا عبر واتساب.' };
};

// ── Customer auth ─────────────────────────────────────────────────────
export const loginUser = async (email, password) => {
  const response = await safeFetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(response);
};

export const registerUser = async (userData) => {
  const response = await safeFetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  return handleResponse(response);
};

/** POST /auth/refresh — the refresh token rotates (the old one is revoked). */
export const refreshSession = async (refreshToken) => {
  const response = await fetch(`${API_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return handleResponse(response);
};

/** POST /auth/logout — revokes the access token and, when sent, the refresh token. Never throws. */
export const logoutSession = async (token, refreshToken) => {
  if (!token) return;
  try {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer(token) },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
      keepalive: true,
    });
  } catch { /* signed out locally anyway */ }
};

// E-mail verification (G69, API_CONTRACT §1a). Errors carry `code`:
// verification_code_invalid | verification_code_expired |
// verification_attempts_exceeded (verify); rate_limited | email_unavailable (resend).
export const verifyEmailCode = async (token, code) =>
  handleResponse(await safeFetch(`${API_URL}/auth/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify({ code }),
  }));

export const resendVerificationEmail = async (token) =>
  handleResponse(await safeFetch(`${API_URL}/auth/verify-email/resend`, {
    method: 'POST',
    headers: bearer(token),
  }));

export const getCurrentUser = async (token) =>
  handleResponse(await fetch(`${API_URL}/auth/me`, { headers: bearer(token) }));

// POST /auth/delete-account {password} | {google_id_token} (G75, accounts
// without a password) → {message}. 401 wrong password / google_reauth_required,
// 409 while an order is still being fulfilled, 429 rate-limited.
export const deleteAccount = async (token, proof) =>
  handleResponse(await fetch(`${API_URL}/auth/delete-account`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify(typeof proof === 'string' ? { password: proof } : proof),
  }));

// GET /auth/export-data → JSON of everything kept about the signed-in
// customer (profile, addresses, measurements, orders, reviews, wishlist,
// loyalty). Rate-limited to 5 per hour (429).
export const exportMyData = async (token) =>
  handleResponse(await fetch(`${API_URL}/auth/export-data`, { headers: bearer(token), cache: 'no-store' }));

// GET | PUT /customer-experience/email-preferences → {marketing_emails: bool, marketing_consent}.
// Covers cart reminders and birthday e-mails; order e-mails are always sent.
export const getEmailPreferences = async (token) =>
  handleResponse(await fetch(`${API_URL}/customer-experience/email-preferences`, { headers: bearer(token) }));

export const setEmailPreferences = async (token, marketingEmails) =>
  handleResponse(await fetch(`${API_URL}/customer-experience/email-preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    // `source` is kept on the server's consent record (G50).
    body: JSON.stringify({ marketing_emails: Boolean(marketingEmails), source: 'web' }),
  }));

// ── Help & policy content ─────────────────────────────────────────────
// Public GET /content/{faq|about|returns|shipping|terms} (backend
// routes/content.py): EN + AR text shared with the app, with the facts
// (return window, delivery zones, COD cap, payment methods…) filled in by the
// server from the code that enforces them.
export const CONTENT_SLUGS = ['faq', 'about', 'returns', 'shipping', 'terms'];
export const getSiteContent = async (slug, { signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/content/${encodeURIComponent(slug)}`, { signal }));

// Buying guides (backend utils/guides.py): {items: [summary], total}, one guide
// with its sections, and a category page's internal links (API_CONTRACT §2i).
export const getGuides = async ({ category, signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/content/guides${category ? `?category=${encodeURIComponent(category)}` : ''}`, { signal }));

export const getGuide = async (slug, { signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/content/guides/${encodeURIComponent(slug)}`, { signal }));

export const getCategoryHub = async (category, { signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/content/hub/${encodeURIComponent(category)}`, { signal }));

// ── Banners ───────────────────────────────────────────────────────────
// Public GET /banners/active: bare list sorted by sort_order (see utils/banners.js).
export const getActiveBanners = async (position = 'hero', { signal } = {}) =>
  getPublicJson(`/banners/active?position=${encodeURIComponent(position)}`, { signal });

// GET /promotions/flash-sales/active → {flash_sales: [sale]} (API_CONTRACT §2c):
// live sales, soonest ending first, with public products and their real
// prices. The hero and the sale strip, asking together, share one request,
// which public/index.html may already have started on "/". The strip mounts
// later (its own chunk), so an answer is reused for FLASH_SALES_REUSE_MS
// with each `time_remaining` lowered by the time since it arrived: the
// countdown stays exact and the home page asks once instead of twice.
export const FLASH_SALES_REUSE_MS = 15000;
const flashClock = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
let flashSalesAnswer = null; // {value, at}

const agedFlashSales = (value, elapsedSeconds) => {
  if (!value || !Array.isArray(value.flash_sales)) return value;
  return {
    ...value,
    flash_sales: value.flash_sales.map((sale) => (sale && typeof sale === 'object' && sale.time_remaining != null
      ? { ...sale, time_remaining: Math.max(0, (Number(sale.time_remaining) || 0) - elapsedSeconds) }
      : sale)),
  };
};

export const getActiveFlashSales = async ({ signal } = {}) => {
  const hit = flashSalesAnswer;
  const elapsed = hit ? flashClock() - hit.at : Infinity;
  if (hit && elapsed >= 0 && elapsed < FLASH_SALES_REUSE_MS) {
    if (signal?.aborted) throw abortError();
    return agedFlashSales(hit.value, elapsed / 1000);
  }
  const value = await getJson(`${API_URL}/promotions/flash-sales/active`, { signal });
  flashSalesAnswer = { value, at: flashClock() };
  return value;
};

/** Forget the reused flash-sale answer (tests). */
export const clearFlashSalesAnswer = () => { flashSalesAnswer = null; };

// ── Wishlist ──────────────────────────────────────────────────────────
export const getWishlist = async (token) =>
  handleResponse(await fetch(`${API_URL}/wishlist/`, { headers: bearer(token) }));

export const removeWishlistItem = async (token, productId) =>
  handleResponse(await fetch(`${API_URL}/wishlist/remove/${encodeURIComponent(productId)}`, {
    method: 'DELETE',
    headers: bearer(token),
  }));

export const clearWishlistItems = async (token) =>
  handleResponse(await fetch(`${API_URL}/wishlist/clear`, { method: 'POST', headers: bearer(token) }));

// ── Wishlist alerts ───────────────────────────────────────────────────
// PATCH /wishlist/{product_id} {notify_on_stock?, notify_on_sale?} →
// {product_id, notify_on_sale, notify_on_stock}. Own items only (404 otherwise).
export const updateWishlistAlerts = async (token, productId, { notifyOnStock, notifyOnSale } = {}) => {
  const body = {};
  if (typeof notifyOnStock === 'boolean') body.notify_on_stock = notifyOnStock;
  if (typeof notifyOnSale === 'boolean') body.notify_on_sale = notifyOnSale;
  return handleResponse(await fetch(`${API_URL}/wishlist/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify(body),
  }));
};

// ── Cart ──────────────────────────────────────────────────────────────
export const getCart = async (token) =>
  handleResponse(await fetch(`${API_URL}/cart`, { headers: bearer(token) }));

/**
 * POST /cart/add. The server keeps one line per (product, size, colour)
 * (G16): `variantId` is the web's "<colour>|<size>" and is sent both as
 * `size` / `color` and, for compatibility, as `variant_id`.
 */
export const addToCart = async (token, productId, variantId = null, quantity = 1) => {
  const { size, color } = decodeVariant(variantId);
  const response = await fetch(`${API_URL}/cart/add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify({
      product_id: productId,
      variant_id: variantId,
      ...(size ? { size } : {}),
      ...(color ? { color } : {}),
      quantity,
    }),
  });
  return handleResponse(response);
};

/** PUT /cart/{line id} — `lineId` is the GET /cart line `id` (a product id still works for single-line products). */
export const updateCartQuantity = async (token, lineId, quantity) => {
  const response = await fetch(`${API_URL}/cart/${encodeURIComponent(lineId)}?quantity=${encodeURIComponent(quantity)}`, {
    method: 'PUT',
    headers: bearer(token),
  });
  return handleResponse(response);
};

/** DELETE /cart/{line id} — removes one line (a product id removes all of that product's lines). */
export const removeFromCart = async (token, lineId) => {
  const response = await fetch(`${API_URL}/cart/${encodeURIComponent(lineId)}`, {
    method: 'DELETE',
    headers: bearer(token),
  });
  return handleResponse(response);
};

export const clearCart = async (token) =>
  handleResponse(await fetch(`${API_URL}/cart`, { method: 'DELETE', headers: bearer(token) }));

/**
 * POST /cart/save-abandoned — JSON body `{email, total, user_id, items}`.
 * The e-mail and account id never go in the URL (request logs keep query
 * strings; security audit 6 Oct). Uses keepalive so it can run from a
 * beforeunload handler. Never throws.
 * A signed-in shopper's `token` is sent as the bearer so the backend binds
 * the cart to that account (an expired token is ignored there, and the
 * user_id + email pair still binds it).
 */
export const saveAbandonedCart = async ({ email, total, userId, items, token }) => {
  if (!email || !Array.isArray(items) || !items.length) return false;
  try {
    const body = { email, total: Number(Number(total || 0).toFixed(2)), items: items.slice(0, 50) };
    if (userId) body.user_id = String(userId);
    const response = await fetch(`${API_URL}/cart/save-abandoned`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer(token) },
      body: JSON.stringify(body),
      keepalive: true,
    });
    return response.ok;
  } catch {
    return false;
  }
};

// ── Coupons ───────────────────────────────────────────────────────────
/**
 * POST /coupons/validate. Signed-in shoppers are identified by their token;
 * guests pass the checkout email (welcome coupons are bound to an address).
 * Resolves to {ok: true, discount, code, message} or {ok: false, status, message}.
 */
export const validateCoupon = async ({ code, cartTotal, email, token }) => {
  try {
    const response = await fetch(`${API_URL}/coupons/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer(token) },
      body: JSON.stringify({
        code: String(code || '').trim().toUpperCase(),
        cart_total: Number(cartTotal || 0),
        ...(!token && email ? { email: String(email).trim() } : {}),
      }),
    });
    const data = await response.json().catch(() => null);
    if (response.ok && data) {
      return { ok: true, discount: Number(data.discount || 0), code: data.code, message: data.message };
    }
    let message = detailText(data) || 'Invalid coupon code';
    if (response.status === 429) message = 'Too many coupon attempts. Please wait a few minutes and try again.';
    else if (response.status === 403 && !token && !email) {
      message = `${message} If this code was emailed to you, enter your email at checkout first.`;
    } else if (response.status >= 500) message = 'Could not check the coupon right now. Please try again.';
    return { ok: false, status: response.status, message };
  } catch {
    return { ok: false, status: 0, message: 'Could not check the coupon. Check your connection and try again.' };
  }
};

// ── Orders ────────────────────────────────────────────────────────────
/** A random UUID v4 for the Idempotency-Key header (one per checkout attempt). */
export const newIdempotencyKey = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * POST /orders (signed in) or /orders/guest with an Idempotency-Key.
 * The same key must be reused when retrying the same checkout attempt: the
 * server then replays the original order instead of creating a second one.
 * Resolves to {ok, status, data, code}; never throws on HTTP errors.
 */
export const submitOrder = async ({ token, payload, idempotencyKey, headers = {} }) => {
  const response = await fetch(`${API_URL}${token ? '/orders' : '/orders/guest'}`, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
      ...bearer(token),
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  return {
    ok: response.ok,
    status: response.status,
    data: data || {},
    code: data?.code || response.headers?.get?.('X-Error-Code') || undefined,
  };
};

export const createOrder = async (token, orderData, idempotencyKey) => {
  const result = await submitOrder({ token, payload: orderData, idempotencyKey });
  if (!result.ok) {
    const err = new Error(apiErrorMessage(result.status, result.data));
    err.status = result.status;
    err.code = result.code;
    err.body = result.data;
    throw err;
  }
  return result.data;
};

export const getUserOrders = async (token) =>
  handleResponse(await fetch(`${API_URL}/orders/my`, { headers: bearer(token) }));

export const getOrder = async (token, orderId) =>
  handleResponse(await fetch(`${API_URL}/orders/${encodeURIComponent(orderId)}`, { headers: bearer(token) }));

/** GET /orders/my?skip=&limit= — one page, newest first (no total: a short page is the last). */
export const getMyOrdersPage = async (token, { skip = 0, limit = 20 } = {}) =>
  handleResponse(await fetch(
    `${API_URL}/orders/my?skip=${encodeURIComponent(skip)}&limit=${encodeURIComponent(limit)}`,
    { headers: bearer(token) },
  ));

/**
 * POST /orders/{id}/cancel (G49). Only offered while the order's server flag
 * `can_cancel` is true; a 409 carries `code` `not_cancellable` or `order_changed`.
 */
export const cancelMyOrder = async (token, orderId) =>
  handleResponse(await fetch(`${API_URL}/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify({}),
  }));

/**
 * PUT /orders/{id}/address (owner decision 13 Oct): the customer changes the
 * delivery address until the order ships (`can_change_address`), within the
 * same emirate / country. 409 `address_locked` | `address_area_changed`.
 */
export const changeMyOrderAddress = async (token, orderId, address) =>
  handleResponse(await fetch(`${API_URL}/orders/${encodeURIComponent(orderId)}/address`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify({ delivery_address: address }),
  }));

// ── Returns ───────────────────────────────────────────────────────────
/** GET /returns/eligibility/{order_id} (G49): window and per-line quantities left. */
export const getReturnEligibility = async (token, orderId) =>
  handleResponse(await fetch(`${API_URL}/returns/eligibility/${encodeURIComponent(orderId)}`, { headers: bearer(token) }));

/** POST /returns — `{order_id, items: [{product_id, quantity, size?, color?}], reason, description, images}`. */
export const createReturnRequest = async (token, payload) =>
  handleResponse(await fetch(`${API_URL}/returns`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify(payload),
  }));

/** GET /returns/my → `{returns, count, total, skip, limit}`. */
export const getMyReturns = async (token) =>
  handleResponse(await fetch(`${API_URL}/returns/my`, { headers: bearer(token) }));

/** GET /returns/{id}/label: own approved return's label data (409 `label_not_available` otherwise). */
export const getReturnLabel = async (token, returnId) =>
  handleResponse(await fetch(`${API_URL}/returns/${encodeURIComponent(returnId)}/label`, { headers: bearer(token) }));

/** POST /returns/{id}/label/printed → `{label_printed_at}` (first print only). */
export const markReturnLabelPrinted = async (token, returnId) =>
  handleResponse(await fetch(`${API_URL}/returns/${encodeURIComponent(returnId)}/label/printed`, { method: 'POST', headers: bearer(token) }));

// ── Admin auth ────────────────────────────────────────────────────────
/**
 * POST /admin/login. When 2FA is on the server answers 401 "Two-factor code
 * required" (no code sent) or "Invalid two-factor code" (wrong code); the
 * thrown error then has `needs2fa` (and `invalid2fa`) set. Success returns
 * {token, admin: {id, email, full_name, role, permissions, totp_enabled, …}}.
 */
export const adminLogin = async (email, password, totpCode) => {
  const code = (totpCode || '').replace(/\s+/g, '');
  let response;
  try {
    response = await fetch(`${API_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(code ? { email, password, totp_code: code } : { email, password }),
    });
  } catch {
    const err = new Error('Cannot reach the server. Check your connection. It can take up to a minute to wake up.');
    err.status = 0;
    throw err;
  }
  try {
    return await handleResponse(response);
  } catch (err) {
    const twoFactor = err.status === 401 && (
      /two-factor/i.test(err.detail || '') || response.headers?.get?.('X-2FA-Required') === 'true'
    );
    if (twoFactor) {
      err.needs2fa = true;
      err.invalid2fa = /invalid/i.test(err.detail || '');
      err.message = err.invalid2fa
        ? 'That code did not work. Enter the current 6-digit code or a backup code.'
        : 'Enter the 6-digit code from your authenticator app.';
    }
    throw err;
  }
};

// ── Admin: dashboard & orders ─────────────────────────────────────────
export const getAdminDashboard = (token) => adminRequest(token, '/admin/dashboard/stats');

export const getAdminOrders = (token, status = null, { limit = 500, skip } = {}) =>
  adminRequest(token, '/admin/orders', { query: { status, limit, skip } });

export const updateOrderStatus = (token, orderId, status, { trackingNumber, notes } = {}) =>
  adminRequest(token, `/admin/orders/${encodeURIComponent(orderId)}/status`, {
    method: 'PUT',
    query: { status, tracking_number: trackingNumber, notes },
  });

/** E4: idempotent — COD delivered → paid, refunded → refunded. */
export const backfillPaymentStatus = (token) =>
  adminRequest(token, '/admin/orders/backfill-payment-status', { method: 'POST' });

// ── Admin: products ───────────────────────────────────────────────────
export const createProduct = (token, productData) =>
  adminRequest(token, '/products', { method: 'POST', body: productData });

export const updateProduct = (token, productId, productData) =>
  adminRequest(token, `/products/${encodeURIComponent(productId)}`, { method: 'PUT', body: productData });

export const deleteProduct = (token, productId) =>
  adminRequest(token, `/products/${encodeURIComponent(productId)}`, { method: 'DELETE' });

// ── Admin: analytics, coupons, reviews, inventory, customers ──────────
export const getAdminAnalytics = (token, period = '30d') =>
  adminRequest(token, '/admin/analytics', { query: { period } });

export const getAdminCoupons = (token) => adminRequest(token, '/admin/coupons');

export const createAdminCoupon = (token, params) =>
  adminRequest(token, '/admin/coupons', { method: 'POST', query: params });

export const updateAdminCoupon = (token, code, active) =>
  adminRequest(token, `/admin/coupons/${encodeURIComponent(code)}`, { method: 'PUT', query: { active } });

export const deleteAdminCoupon = (token, code) =>
  adminRequest(token, `/admin/coupons/${encodeURIComponent(code)}`, { method: 'DELETE' });

export const getAdminReviews = (token, status = null) =>
  adminRequest(token, '/admin/reviews', { query: { status } });

export const updateAdminReviewStatus = (token, reviewId, status) =>
  adminRequest(token, `/admin/reviews/${encodeURIComponent(reviewId)}/status`, { method: 'PUT', query: { status } });

export const deleteAdminReview = (token, reviewId) =>
  adminRequest(token, `/admin/reviews/${encodeURIComponent(reviewId)}`, { method: 'DELETE' });

export const getAdminInventory = (token) => adminRequest(token, '/admin/inventory');

export const adjustAdminStock = (token, productId, adjustment, reason = 'manual') =>
  adminRequest(token, '/admin/inventory/adjust', {
    method: 'POST',
    query: { product_id: productId, adjustment, reason },
  });

export const getAdminStockMovements = (token, productId) =>
  adminRequest(token, `/admin/inventory/movements/${encodeURIComponent(productId)}`);

export const getAdminCustomers = (token, skip = 0, limit = 100) =>
  adminRequest(token, '/admin/customers', { query: { skip, limit } });

// ── Admin: staff (super_admin only) ───────────────────────────────────
// JSON bodies, so a new staff member's password never appears in a URL.
export const listStaff = (token) => adminRequest(token, '/admin/staff');

export const createStaff = (token, { email, full_name, password, role, permissions }) =>
  adminRequest(token, '/admin/staff', { method: 'POST', body: { email, full_name, password, role, permissions } });

export const updateStaff = (token, staffId, changes) =>
  adminRequest(token, `/admin/staff/${encodeURIComponent(staffId)}`, { method: 'PUT', body: changes });

export const deactivateStaff = (token, staffId) =>
  adminRequest(token, `/admin/staff/${encodeURIComponent(staffId)}`, { method: 'DELETE' });

// ── Brands & settings ─────────────────────────────────────────────────
export const getBrands = async () => handleResponse(await fetch(`${API_URL}/brands`));

export const getStoreSettings = async () => handleResponse(await fetch(`${API_URL}/settings`));

export const bulkUpdateSettings = (token, updates) =>
  adminRequest(token, '/settings/bulk', { method: 'POST', body: updates });

export const getShippingZones = async () => handleResponse(await fetch(`${API_URL}/settings/shipping/zones`));

// ── Reports ───────────────────────────────────────────────────────────
export const getPnLReport = (token, startDate, endDate) =>
  adminRequest(token, '/admin/reports/pnl', { query: { start_date: startDate, end_date: endDate } });

export const getStockValuationReport = (token) => adminRequest(token, '/admin/reports/stock-valuation');

export const getProductPerformanceReport = (token, period = '30d') =>
  adminRequest(token, '/admin/reports/product-performance', { query: { period } });

export const getCustomerAnalyticsReport = (token, period = '30d') =>
  adminRequest(token, '/admin/reports/customer-analytics', { query: { period } });

// ── Expenses & purchase orders ────────────────────────────────────────
export const getExpenses = (token, params = {}) => adminRequest(token, '/admin/expenses', { query: params });

export const getPurchaseOrders = (token, status = null) =>
  adminRequest(token, '/admin/purchase-orders', { query: { status } });

// ── Body fit (size guides, measurements, size recommendation) ────────
// API_CONTRACT §2b. Measurements are private: only the owner's token reads them.
export const getProductSizeGuide = async (productId) =>
  handleResponse(await fetch(`${API_URL}/products/${encodeURIComponent(productId)}/size-guide`));

export const getSizeRecommendation = async (token, productId) =>
  handleResponse(await fetch(`${API_URL}/products/${encodeURIComponent(productId)}/size-recommendation`, {
    headers: bearer(token),
  }));

export const getMeasurements = async (token) =>
  handleResponse(await fetch(`${API_URL}/auth/measurements`, { headers: bearer(token) }));

export const saveMeasurements = async (token, measurements) =>
  handleResponse(await fetch(`${API_URL}/auth/measurements`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...bearer(token) },
    body: JSON.stringify(measurements),
  }));

// Admin (RBAC area `products`).
export const listAdminSizeGuides = (token) => adminRequest(token, '/admin/size-guides');

export const createAdminSizeGuide = (token, guide) =>
  adminRequest(token, '/admin/size-guides', { method: 'POST', body: guide });

export const updateAdminSizeGuide = (token, slug, guide) =>
  adminRequest(token, `/admin/size-guides/${encodeURIComponent(slug)}`, { method: 'PUT', body: guide });

export const deleteAdminSizeGuide = (token, slug) =>
  adminRequest(token, `/admin/size-guides/${encodeURIComponent(slug)}`, { method: 'DELETE' });

// Flash sales (RBAC area `marketing`, backend routes/promotions.py).
export const listAdminFlashSales = (token) => adminRequest(token, '/promotions/flash-sales');

export const createAdminFlashSale = (token, sale) =>
  adminRequest(token, '/promotions/flash-sales', { method: 'POST', body: sale });

export const updateAdminFlashSale = (token, id, sale) =>
  adminRequest(token, `/promotions/flash-sales/${encodeURIComponent(id)}`, { method: 'PUT', body: sale });

export const deleteAdminFlashSale = (token, id) =>
  adminRequest(token, `/promotions/flash-sales/${encodeURIComponent(id)}`, { method: 'DELETE' });

// Mobile app remote config (backend routes/app_config.py, API_CONTRACT §6b):
// GET is public; PUT is the `settings` RBAC area (partial update, validated).
export const getAdminAppConfig = (token) => adminRequest(token, '/app/config');

export const updateAdminAppConfig = (token, body) =>
  adminRequest(token, '/app/config', { method: 'PUT', body });

// Client messages (POST /contact enquiries and appointment requests; RBAC
// area `customers`).
export const getContactMessages = (token, { topic, status, limit = 200 } = {}) =>
  adminRequest(token, '/contact/messages', { query: { topic, status, limit } });

export const updateContactMessage = (token, id, status) =>
  adminRequest(token, `/contact/messages/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status } });

// ── Admin → Inbox: assistant conversations (API_CONTRACT §6e, area `customers`) ──
const inboxPath = (id, action = '') => `/admin/inbox/conversations/${encodeURIComponent(id)}${action}`;

export const getInboxConversations = (token, { status, channel, unread, limit = 100 } = {}) =>
  adminRequest(token, '/admin/inbox/conversations', { query: { status, channel, unread: unread ? 1 : undefined, limit } });

export const getInboxConversation = (token, id) => adminRequest(token, inboxPath(id));

export const replyInboxConversation = (token, id, text) =>
  adminRequest(token, inboxPath(id, '/reply'), { method: 'POST', body: { text } });

export const setInboxAiPaused = (token, id, paused) =>
  adminRequest(token, inboxPath(id, '/ai'), { method: 'POST', body: { paused } });

export const closeInboxConversation = (token, id) =>
  adminRequest(token, inboxPath(id, '/close'), { method: 'POST' });

export const getInboxSettings = (token) => adminRequest(token, '/admin/inbox/settings');

export const updateInboxSettings = (token, retentionDays) =>
  adminRequest(token, '/admin/inbox/settings', { method: 'PUT', body: { retention_days: retentionDays } });

// Product choices for pickers (admin token: hidden products included).
export const listAdminProductOptions = (token) =>
  adminRequest(token, '/products', { query: { limit: 500, sort: 'newest' } });


// ── Discover (API_CONTRACT §2g) ───────────────────────────────────────
// Brands (public reads; writes are the `brands` RBAC area) and curated
// collections (public reads; admin CRUD is the `marketing` area).
export const getActiveBrands = async ({ signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/brands?active_only=true&limit=1000`, { signal }));

export const getBrand = async (slug, { signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/brands/${encodeURIComponent(slug)}`, { signal }));

export const getCollections = async ({ signal } = {}) => getPublicJson('/collections', { signal });

export const getCollection = async (slug, { signal } = {}) =>
  handleResponse(await fetch(`${API_URL}/collections/${encodeURIComponent(slug)}`, { signal }));

/** One page of a collection's pieces → {items, total, skip, limit}. */
export const getCollectionProducts = async (slug, { skip = 0, limit = 24, sort } = {}, { signal } = {}) => {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  if (sort) params.append('sort', sort);
  return handleResponse(await fetch(
    `${API_URL}/collections/${encodeURIComponent(slug)}/products?${params.toString()}`,
    { signal },
  ));
};

export const createAdminBrand = (token, brand) =>
  adminRequest(token, '/brands', { method: 'POST', body: brand });

export const updateAdminBrand = (token, id, brand) =>
  adminRequest(token, `/brands/${encodeURIComponent(id)}`, { method: 'PUT', body: brand });

export const listAdminCollections = (token) => adminRequest(token, '/admin/collections');

export const createAdminCollection = (token, collection) =>
  adminRequest(token, '/admin/collections', { method: 'POST', body: collection });

export const updateAdminCollection = (token, id, collection) =>
  adminRequest(token, `/admin/collections/${encodeURIComponent(id)}`, { method: 'PUT', body: collection });

export const deleteAdminCollection = (token, id) =>
  adminRequest(token, `/admin/collections/${encodeURIComponent(id)}`, { method: 'DELETE' });

// Seasonal campaigns (API_CONTRACT §2i) — Admin → Campaigns.
export const listAdminCampaigns = (token) => adminRequest(token, '/admin/campaigns');

export const getAdminCampaignOptions = (token) => adminRequest(token, '/admin/campaigns/options');

export const createAdminCampaign = (token, campaign) =>
  adminRequest(token, '/admin/campaigns', { method: 'POST', body: campaign });

export const updateAdminCampaign = (token, id, campaign) =>
  adminRequest(token, `/admin/campaigns/${encodeURIComponent(id)}`, { method: 'PUT', body: campaign });

export const deleteAdminCampaign = (token, id) =>
  adminRequest(token, `/admin/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' });
